export interface BrowserControlTarget {
  readonly threadId: string;
  readonly browserId: string;
  readonly sessionEpoch: string;
  readonly lastAppliedControlSequence: number;
}

export interface BrowserControlIdentity {
  readonly sessionEpoch: string;
  readonly controlSequence: number;
}

interface BrowserControlState {
  readonly sessionEpoch: string;
  nextControlSequence: number;
  tail: Promise<void>;
}

interface EnqueueBrowserControlInput<Result> {
  readonly threadId: string;
  readonly browserId: string;
  readonly execute: (identity: BrowserControlIdentity) => Promise<Result>;
}

export interface BrowserControlSequencer {
  readonly register: (target: BrowserControlTarget) => void;
  readonly enqueue: <Result>(input: EnqueueBrowserControlInput<Result>) => Promise<Result>;
  readonly remove: (threadId: string, browserId?: string) => void;
  readonly dispose: () => void;
}

function browserKey(threadId: string, browserId: string): string {
  return `${threadId}\0${browserId}`;
}

export function createBrowserControlSequencer(): BrowserControlSequencer {
  const states = new Map<string, BrowserControlState>();
  let disposed = false;

  return {
    register(target): void {
      if (disposed) throw new Error("Browser control sequencer is disposed.");
      const key = browserKey(target.threadId, target.browserId);
      const current = states.get(key);
      const authoritativeNext = target.lastAppliedControlSequence + 1;
      if (current?.sessionEpoch === target.sessionEpoch) {
        current.nextControlSequence = Math.max(current.nextControlSequence, authoritativeNext);
        return;
      }
      states.set(key, {
        sessionEpoch: target.sessionEpoch,
        nextControlSequence: authoritativeNext,
        tail: Promise.resolve(),
      });
    },
    enqueue<Result>(input: EnqueueBrowserControlInput<Result>): Promise<Result> {
      if (disposed) return Promise.reject(new Error("Browser control sequencer is disposed."));
      const key = browserKey(input.threadId, input.browserId);
      const state = states.get(key);
      if (!state) {
        return Promise.reject(new Error("Browser must be opened before sending controls."));
      }
      const operation = state.tail.then(async () => {
        if (disposed || states.get(key) !== state) {
          throw new Error("Browser session changed before the queued control could be sent.");
        }
        const controlSequence = state.nextControlSequence;
        const result = await input.execute({
          sessionEpoch: state.sessionEpoch,
          controlSequence,
        });
        state.nextControlSequence = Math.max(state.nextControlSequence, controlSequence + 1);
        return result;
      });
      state.tail = operation.then(
        () => undefined,
        () => undefined,
      );
      return operation;
    },
    remove(threadId, browserId): void {
      if (browserId) {
        states.delete(browserKey(threadId, browserId));
        return;
      }
      const prefix = `${threadId}\0`;
      for (const key of states.keys()) {
        if (key.startsWith(prefix)) states.delete(key);
      }
    },
    dispose(): void {
      disposed = true;
      states.clear();
    },
  };
}
